<script setup lang="ts">
import { isValidForm } from '@/helper'
import { getDefaultAuthenticatedRoute } from '@/navigation/vertical'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import petBeard from '@images/pets/beard.png'
import petCat from '@images/pets/cat.png'
import petDog from '@images/pets/dog.png'
import petFish from '@images/pets/fish.png'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { VForm } from 'vuetify/components'
import {Login} from '@/api/authApi'
import { LoginType } from '@/type/Auth'
import { isNonEmptyStringAndNotWhitespace } from '@sindresorhus/is'


definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const refForm=ref<VForm>()
const REMEMBER_LOGIN_KEY = 'alkokh_remember_login'
const REMEMBER_USERNAME_KEY = 'alkokh_remember_username'

const form = ref({
  email: '',
  password: '',
  remember: false,
})

const message=ref('')
const isPasswordVisible = ref(false)
const loading=ref(false) 
const router=useRouter()
const route = useRoute()
const { t } = useI18n()
const appTitle = computed(() => t(themeConfig.app.title))

onMounted(() => {
  const remembered = localStorage.getItem(REMEMBER_LOGIN_KEY) === '1'

  form.value.remember = remembered

  if (remembered)
    form.value.email = localStorage.getItem(REMEMBER_USERNAME_KEY) || ''
})

function saveRememberedLogin() {
  if (!form.value.remember) {
    localStorage.removeItem(REMEMBER_LOGIN_KEY)
    localStorage.removeItem(REMEMBER_USERNAME_KEY)
    return
  }

  localStorage.setItem(REMEMBER_LOGIN_KEY, '1')
  localStorage.setItem(REMEMBER_USERNAME_KEY, form.value.email.trim())
}

const animalSlides = [
  { src: petCat, alt: 'Cat' },
  { src: petDog, alt: 'Dog' },
  { src: petFish, alt: 'Fish' },
  { src: petBeard, alt: 'Bearded dragon' },
]

const floatingPetIcons = [
  { icon: 'tabler-paw-filled', class: 'pet-float-icon pet-float-icon-1' },
  { icon: 'tabler-bone', class: 'pet-float-icon pet-float-icon-2' },
  { icon: 'tabler-fish', class: 'pet-float-icon pet-float-icon-3' },
  { icon: 'tabler-heart-filled', class: 'pet-float-icon pet-float-icon-4' },
  { icon: 'tabler-dog', class: 'pet-float-icon pet-float-icon-5' },
  { icon: 'tabler-cat', class: 'pet-float-icon pet-float-icon-6' },
]

  async function handleLogin() {
    let body:LoginType={
      usr:form.value.email,
      pwd:form.value.password
    }
    let result= await Login(loading, body, { remember: form.value.remember })
    if(result===200) {
     saveRememberedLogin()
     const redirectTarget = typeof route.query.redirect === 'string' && route.query.redirect
       ? route.query.redirect
       : getDefaultAuthenticatedRoute()

     router.push(redirectTarget)
    }
    else
      message.value='user-name  or password uncorrected.'
  }

  async function submit(){
    if(!await isValidForm(refForm))
      return
    await handleLogin()
  }

const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
</script>

<template>
  <!-- SINGLE ROOT REQUIRED — see pet-profile/list/index.vue for the full reason.
       This page had 2 roots (the logo anchor + the auth VRow), making its root a
       Fragment, which deadlocks the blank layout's <Transition> on leave.
       ⚠️ This wrapper must stay UNPOSITIONED: `.auth-logo` inside is
       `position: absolute`, and adding `position: relative` here would make this
       div its containing block and move the logo. -->
  <div class="auth-page-root">
  <a href="javascript:void(0)">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ appTitle }}
      </h1>
    </div>
  </a>

  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="login-pet-scene d-flex align-center justify-center w-100 h-100"
          aria-hidden="true"
        >
          <div class="pet-orbit">
            <span
              v-for="petIcon in floatingPetIcons"
              :key="petIcon.class"
              :class="petIcon.class"
            >
              <VIcon :icon="petIcon.icon" />
            </span>
          </div>

          <div class="pet-portrait-stage">
            <div class="pet-portrait-glow" />
            <div
              v-for="(animal, index) in animalSlides"
              :key="animal.alt"
              class="pet-slide"
              :style="{ animationDelay: `${index * 3.2}s` }"
            >
              <VImg
                :src="animal.src"
                :alt="animal.alt"
                class="pet-slide-img"
                cover
              />
            </div>
          </div>
        </div>

        <img
          class="auth-footer-mask flip-in-rtl"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-6"
        :loading="loading"
      > 
      <VCardText v-if="isNonEmptyStringAndNotWhitespace(message)">
             <VAlert icon="tabler-exclamation-circle" color="error" variant="outlined" :text="message"/>
      </VCardText>
        <VCardText>
          <h4 class="text-h4 mb-1 text-capitalize">
            {{ t("alkokh.login.title", { app: appTitle }) }} 👋🏻
          </h4>
          <p class="mb-0">
            {{ t("alkokh.login.subtitle") }}
          </p>
        </VCardText>
        <VCardText>
          <VForm ref="refForm" @submit.prevent="submit()">
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  autofocus
                  :label="t('alkokh.login.emailOrUsername')"
                  type="text"
                  autocomplete="username"
                  placeholder="user@example.com"
                />
              </VCol>
              
              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :label="t('alkokh.login.password')"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="current-password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center flex-wrap my-6">
                  <VCheckbox
                    v-model="form.remember"
                    :label="t('alkokh.login.rememberMe')"
                  />
                </div>

                <VBtn
                  block
                  type="submit"
                >
                  {{ t("alkokh.login.signIn") }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";

.login-pet-scene {
  overflow: hidden;
  padding-block: 5.5rem 4rem;
  padding-inline: clamp(3rem, 7vw, 7rem);
}

.pet-portrait-stage {
  position: relative;
  inline-size: min(34rem, 72%);
  aspect-ratio: 1;
  border-radius: 50%;
  isolation: isolate;
}

.pet-portrait-stage::before {
  position: absolute;
  inset: 11%;
  border: 1px solid rgba(var(--v-theme-primary), 0.18);
  border-radius: inherit;
  animation: petRingPulse 4.8s ease-in-out infinite;
  content: "";
}

.pet-portrait-glow {
  position: absolute;
  inset: 7%;
  border-radius: inherit;
  background:
    radial-gradient(circle at 48% 42%, rgba(var(--v-theme-primary), 0.18), transparent 54%),
    radial-gradient(circle at 56% 64%, rgba(var(--v-theme-warning), 0.18), transparent 46%);
  filter: blur(2px);
  transform: scale(1.04);
}

.pet-slide {
  position: absolute;
  inset: 7%;
  display: grid;
  border: 10px solid rgba(var(--v-theme-surface), 0.94);
  border-radius: 50%;
  animation: petSlideShow 12.8s ease-in-out infinite;
  box-shadow: 0 2rem 4.5rem rgba(40, 43, 58, 0.18);
  opacity: 0;
  overflow: hidden;
  place-items: center;
  transform: translateY(18px) scale(0.94) rotate(-2deg);
}

.pet-slide-img {
  inline-size: 100%;
  block-size: 100%;
}

.pet-orbit {
  position: absolute;
  inset: 3rem;
  pointer-events: none;
}

.pet-float-icon {
  position: absolute;
  display: grid;
  inline-size: 3.2rem;
  block-size: 3.2rem;
  border: 1px solid rgba(var(--v-theme-primary), 0.16);
  border-radius: 50%;
  animation: petFloat 5.6s ease-in-out infinite;
  background: rgba(var(--v-theme-surface), 0.78);
  box-shadow: 0 1rem 2rem rgba(40, 43, 58, 0.11);
  color: rgb(var(--v-theme-primary));
  font-size: 1.4rem;
  place-items: center;
}

.pet-float-icon-1 {
  inset-block-start: 13%;
  inset-inline-start: 17%;
}

.pet-float-icon-2 {
  inset-block-start: 19%;
  inset-inline-end: 16%;
  animation-delay: 0.8s;
  color: rgb(var(--v-theme-warning));
}

.pet-float-icon-3 {
  inset-block-end: 21%;
  inset-inline-start: 14%;
  animation-delay: 1.4s;
  color: rgb(var(--v-theme-info));
}

.pet-float-icon-4 {
  inset-block-end: 14%;
  inset-inline-end: 21%;
  animation-delay: 2s;
  color: rgb(var(--v-theme-error));
}

.pet-float-icon-5 {
  inset-block-start: 46%;
  inset-inline-start: 6%;
  animation-delay: 2.6s;
}

.pet-float-icon-6 {
  inset-block-start: 47%;
  inset-inline-end: 7%;
  animation-delay: 3.2s;
  color: rgb(var(--v-theme-success));
}

@keyframes petSlideShow {
  0%,
  24% {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0deg);
  }

  31%,
  100% {
    opacity: 0;
    transform: translateY(-16px) scale(0.96) rotate(2deg);
  }
}

@keyframes petFloat {
  0%,
  100% {
    transform: translate3d(0, 0, 0) rotate(0deg);
  }

  50% {
    transform: translate3d(0, -16px, 0) rotate(7deg);
  }
}

@keyframes petRingPulse {
  0%,
  100% {
    opacity: 0.55;
    transform: scale(1);
  }

  50% {
    opacity: 0.95;
    transform: scale(1.08);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pet-slide,
  .pet-float-icon,
  .pet-portrait-stage::before {
    animation: none;
  }

  .pet-slide:nth-of-type(2) {
    opacity: 1;
    transform: none;
  }
}
</style>
